import { GridApi, GridOptions, GridReadyEvent } from "ag-grid-enterprise";
import { TableParent } from "@bitwarden/web-vault/app/models/types/table.types";

import type { GridSizeChangedEvent } from "ag-grid-community/dist/types/core/events";
import { EarnVersionTableSubscription } from "./earn-version.table.subscription";
import { ColDef as AgColDef } from "ag-grid-community";
import { EarnTableStateEnum } from "@bitwarden/web-vault/app/models/enum/earn.enum";
import { getRowHeight } from "../../../tables.utils.js";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { inject } from "@angular/core";
import { EarnVersion } from "../../../../../models/types/earn.types";
import { ManageCellRendererComponent } from "@bitwarden/web-vault/app/components/tables/earn-tables/renderers/manage-version/manage-cell.renderer";
import { EarnConfigType } from "@bitwarden/web-vault/app/models/types/environement-config.type";
import { DataRepositoryService } from "@bitwarden/web-vault/app/services/DataRepository/data-repository.service";
import { TokenService } from "@bitwarden/common/abstractions/token.service";
import { ConfirmationEnum } from "@bitwarden/web-vault/app/models/enum/confirmation.enum";
import { VersionStateCellRenderer } from "@bitwarden/web-vault/app/components/tables/earn-tables/renderers/version-state/version-state.renderer";
import { VersionGroupCellRenderer } from "@bitwarden/web-vault/app/components/tables/earn-tables/renderers/version-group/version-group.renderer";
import { AppStateStoreService } from "@bitwarden/web-vault/app/services/store/app-state/app-state.store.service";

export interface ColDefExtended<T> extends AgColDef<T> {
  headerComponentFramework?: any;
}

export class EarnVersionTable extends EarnVersionTableSubscription {
  confirmationService = inject(ConfirmationDialogService);
  protected dataRepositoryService = inject(DataRepositoryService);
  protected tokenService = inject(TokenService);
  protected appState = inject(AppStateStoreService);
  gridApi: GridApi;
  options: GridOptions<EarnVersion>;
  columnDefinitions: ColDefExtended<EarnVersion>[];
  /** In-flight guard against double-submits — the blocking overlay no longer blocks input (#5468). */
  private deleting = false;

  constructor(parent: TableParent) {
    super();

    this.columnDefinitions = [
      { field: "groupKey", rowGroup: true, hide: true },
      {
        headerName: "Version",
        colId: "versionCol",

        valueGetter: (p) => {
          if (p.node.group) {
            return String(p.node.key ?? "");
          }
          return p.data?.versionLabel ?? "";
        },
        comparator: (a, b) => String(a ?? "").localeCompare(String(b ?? "")),
        sort: "desc",
        minWidth: 220,
      },

      {
        headerName: "Release State",
        field: "status",
        width: 130,
        cellRenderer: VersionStateCellRenderer,
        sortable: false,
      },

      {
        headerName: "Update Date",
        field: "updatedAt",
        valueFormatter: (p) => this.formatDate(p.value),
        sort: "desc",
        sortingOrder: ["desc", "asc", null],
        width: 150,
      },

      {
        headerName: "Manage",
        field: "maskId",
        cellRenderer: ManageCellRendererComponent,
        cellRendererParams: {
          onView: (row: any) => this.onView(row),
          onEditAsNew: (row: any) => this.onEditAsNew(row),
          onResume: (row: any) => this.onResume(row),
          onDelete: (row: any) => this.onDelete(row),
        },
        sortable: false,
      },
    ];

    // todo clean up the options.. remove comments, unnecessary codes and see if you can bundle it up more

    this.options = {
      context: { parent },
      onGridReady: async (params: GridReadyEvent) => {
        this.gridApi = params.api;
      },
      onGridSizeChanged: (params: GridSizeChangedEvent) => {
        params.api.sizeColumnsToFit();
      },
      onPaginationChanged: (params) => {
        const newPageSize = params.api.paginationGetPageSize();
        this.earnService.defaultPageSize.set(newPageSize);
      },

      rowDragManaged: false,
      suppressRowClickSelection: true,
      suppressCellFocus: true,
      pagination: true,
      paginationPageSize: this.earnService.defaultPageSize(),
      paginationPageSizeSelector: [5, 10, 20, 50, 100],
      defaultColDef: {
        flex: 1, // makes columns auto-fit
        sortable: false, // allow sorting
        filter: false, // enable filtering by default
        resizable: true, // allow column resize
        floatingFilter: false, // ✅ adds search bar under each column name
      },
      columnDefs: this.columnDefinitions,
      getRowHeight: getRowHeight,
      groupDisplayType: "groupRows",
      groupRowRenderer: VersionGroupCellRenderer,
      groupRowRendererParams: {
        suppressCount: true,
        // innerRenderer: VersionGroupCellRenderer,
      },
      groupDefaultExpanded: -1,
      getRowStyle: (params) => {
        return { borderTop: "2px solid #e5e7eb", borderBottom: "2px solid #e5e7eb" };
      },
    };
  }

  private formatDate(iso: string): string {
    if (!iso) {
      return "";
    }
    const d = new Date(iso);
    const y = d.getFullYear();
    const m = (d.getMonth() + 1).toString().padStart(2, "0");
    const day = d.getDate().toString().padStart(2, "0");
    return `${y}-${m}-${day}`;
  }

  onView(row: EarnVersion) {
    const mask = this.earnService.maskList().find((m) => m.maskId === row.maskId);

    this.earnService.selectedMask.set(mask);
    this.earnLookup.maskToLookup(mask);
    this.earnService.activeSection.set("create");
    this.earnService.breadCrumbState.set(EarnTableStateEnum.PUBLISH);
  }

  onEditAsNew(row: EarnVersion) {
    const mask = this.earnService.maskList().find((m) => m.maskId === row.maskId);
    this.earnService.initializeMask({
      ...mask,
      maskId: crypto.randomUUID(),
      publishedAt: null,
      updatedAt: null,
      status: "draft",
    });

    this.earnService.activeSection.set("create");
    this.earnService.breadCrumbState.set(EarnTableStateEnum.DRAFT);
  }

  onResume(row: EarnVersion) {
    const mask = this.earnService.maskList().find((m) => m.maskId === row.maskId);
    this.earnService.initializeMask(mask);
    this.earnService.activeSection.set("create");
    this.earnService.breadCrumbState.set(EarnTableStateEnum.DRAFT);
  }

  async onDelete(row: EarnVersion) {
    // Ignore re-entry while a delete is in flight — the confirm dialog and the delete both run
    // without a blocking overlay now (#5468). Set the guard before the confirm so a second click
    // can't open a second dialog. Nested try/finally: the OUTER finally always releases the guard
    // (even if confirmFor() itself rejects, or the user cancels), so the button can't latch dead;
    // the INNER finally owns the shared "sharing-data" process, so it only runs when a delete
    // actually started — cancelling never calls endProcess (it's by type and would drop a
    // concurrent publish's indicator).
    if (this.deleting) {
      return;
    }
    this.deleting = true;
    try {
      const isDelete = await this.confirmationService.confirmFor(
        ConfirmationEnum.deletingEarnVersion,
      );

      if (!isDelete) {
        return;
      }
      let deleteFailed = false;
      try {
        const processMessage = `Deleting data publication...`;
        this.appState.startProcess("sharing-data", processMessage);

        const mask = this.earnService.maskList().find((m) => m.maskId === row.maskId);
        const userId = await this.tokenService.getUserId();
        const earnConfig = process.env.EARN as unknown as EarnConfigType;
        const url = `${earnConfig.url}/${earnConfig.apiStage}`;

        const maskFile = this.earnService.getMaskFile(mask);
        const path = earnConfig.endpoint.mask.delete
          .replace(":userId", userId)
          .replace(":maskId", maskFile.fileId.replace("/", ""));
        await this.dataRepositoryService.send("DELETE", path, null, true, true, url);

        // Only delete THIS version's bundles — every bundle carries its own maskId, so an
        // unfiltered sweep wipes every version's published data (#5526).
        const bundleFiles = await this.earnService.getJsonBundle();
        for (const f of bundleFiles.items.filter((b: any) => b.data?.maskId === row.maskId)) {
          const path = earnConfig.endpoint.bundle.delete
            .replace(":userId", userId)
            .replace(":bundleId", f.fileId);
          await this.dataRepositoryService.send("DELETE", path, null, true, true, url);
        }
      } catch (error) {
        deleteFailed = true;
        this.confirmationService.notify("error", "failed", "earnVersionDeleteFailed");
      } finally {
        // Refresh the list even on failure so a partial delete can't strand a stale row that a
        // retry would re-DELETE (already-gone fileId) forever (#5526). Its own catch surfaces a
        // refresh failure as a toast instead of letting it escape onDelete as an unhandled
        // rejection; the inner finally keeps the endProcess cleanup guaranteed regardless.
        try {
          await this.earnService.updateState();
        } catch (error) {
          // Don't double-toast if the delete itself already reported a failure (#5526).
          if (!deleteFailed) {
            this.confirmationService.notify("error", "failed", "earnVersionDeleteFailed");
          }
        } finally {
          this.appState.endProcess("sharing-data");
        }
      }
    } finally {
      this.deleting = false;
    }
  }
}
